import { useCallback, useEffect, useRef } from "react";
import { useNotification } from "@context/NotificationContext";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { CrewMember } from "@features/vessel-call/create/store/useVesselCallForm.store";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import type { ImportCrewListResponse, ImportedCrewEntry } from "@services/vesselCall/types";
import { importCrewList } from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

type UseImportCrewListOptions = {
  onSuccess?: (importedCrew: CrewMember[]) => void;
};

type CrewImportError = {
  message?: string;
  response?: {
    data?: {
      detail?: string;
      message?: string;
      errors?: string[];
    };
  };
};

const getCrewOrder = (crewMember: ImportedCrewEntry, fallbackIndex: number) => {
  const order =
    crewMember.no ??
    crewMember.crewOrderToBeDisplayed ??
    crewMember.crewOrder ??
    crewMember.order ??
    crewMember.sequence;

  return typeof order === "number" && Number.isFinite(order) ? order : fallbackIndex + 1;
};

const isMasterCrewMember = (crewMember: ImportedCrewEntry) => {
  const rank = crewMember.rankReferenceCode ?? crewMember.rank ?? "";

  return rank.trim().toUpperCase() === "MASTER";
};

const mapImportedCrew = (crewMember: ImportedCrewEntry, index: number): CrewMember => ({
  id: `imported-crew-${Date.now()}-${index + 1}`,
  order: getCrewOrder(crewMember, index),
  familyName: crewMember.familyName ?? "",
  givenName: crewMember.givenName ?? "",
  rank: crewMember.rankReferenceCode ?? crewMember.rank ?? "",
  nationality: crewMember.nationality ?? "",
  dateOfBirth: crewMember.birthDate ?? "",
  placeOfBirth: crewMember.birthPlace ?? "",
  gender: crewMember.gender ?? "",
  passportNumber: crewMember.passportNumber ?? "",
  issuingState:
    crewMember.identityDocumentIssuingStateReferenceCode ??
    crewMember.identityDocumentIssuingState ??
    "",
  expiryDate: crewMember.identityDocumentExpiryDate ?? "",
});

const extractErrorMessage = (error: CrewImportError, fallback: string) => {
  const backendErrors = error.response?.data?.errors;
  if (Array.isArray(backendErrors) && backendErrors.length > 0) {
    return backendErrors.join(" ");
  }

  return error.response?.data?.detail ?? error.response?.data?.message ?? error.message ?? fallback;
};

const isAbortedRequest = (error: CrewImportError) =>
  (error as { code?: string; name?: string }).code === "ERR_CANCELED" ||
  (error as { code?: string; name?: string }).name === "CanceledError" ||
  (error as { code?: string; name?: string }).name === "AbortError";

const normalizeImportedCrew = (response: ImportCrewListResponse) => {
  const crews = Array.isArray(response) ? response : (response.crews ?? []);

  return [...crews]
    .sort((left, right) => {
      const leftIsMaster = isMasterCrewMember(left);
      const rightIsMaster = isMasterCrewMember(right);

      if (leftIsMaster !== rightIsMaster) {
        return leftIsMaster ? -1 : 1;
      }

      return getCrewOrder(left, 0) - getCrewOrder(right, 0);
    })
    .map(mapImportedCrew);
};

const IMPORT_CREW_LIST_TRANSACTION_CONTEXT = {
  module: "vessel_call",
  screen: "FAL Crew List",
  action: "import_crew_list",
  entityType: "crew_list",
};

export const useImportCrewList = (options: UseImportCrewListOptions = {}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const setCrew = useVesselCallFormStore((state) => state.setCrew);
  const setField = useVesselCallFormStore((state) => state.setField);
  const clearAddCrewDraft = useVesselCallFormStore((state) => state.clearAddCrewDraft);
  const importControllerRef = useRef<AbortController | null>(null);

  const abortImport = useCallback(() => {
    importControllerRef.current?.abort();
  }, []);

  useEffect(() => abortImport, [abortImport]);

  const mutation = useMutation({
    mutationFn: (file: File) => {
      importControllerRef.current?.abort();
      const controller = new AbortController();
      importControllerRef.current = controller;
      return runTransaction(IMPORT_CREW_LIST_TRANSACTION_CONTEXT, (transactionId) =>
        importCrewList(file, controller.signal, transactionId),
      );
    },
    onSuccess: (response: ImportCrewListResponse) => {
      const importedCrew = normalizeImportedCrew(response);

      setCrew(importedCrew);
      setField(FORM_FIELDS.NUMBER_OF_CREW, String(importedCrew.length));
      clearAddCrewDraft();
      options.onSuccess?.(importedCrew);

      notify({
        title: t("vessel-call.create.fal-form-5.import-success-title"),
        description: t("vessel-call.create.fal-form-5.import-success-description"),
        type: "success",
      });
    },
    onError: (error: CrewImportError) => {
      if (isAbortedRequest(error)) {
        return;
      }

      notify({
        title: t("vessel-call.create.fal-form-5.import-error-title"),
        description: extractErrorMessage(
          error,
          t("vessel-call.create.fal-form-5.import-error-description"),
        ),
        type: "error",
      });
    },
  });

  return { ...mutation, abortImport };
};
